import { Injectable, TemplateRef } from '@angular/core';
import { MatDialog, MatDialogConfig, MatDialogRef, MatDialogState } from '@angular/material/dialog';
import { DialogComponent } from '../components/dialog/dialog.component';
import { LoaderComponent } from '../components/loader/loader.component';
import { ComponentType } from '@angular/cdk/portal';
import { KeyboardFocusService } from './keyboard-focus/keyboard-focus.service';
import { map, of, tap } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class DialogsService {


  constructor(
    private dialog: MatDialog,
    private focusService: KeyboardFocusService
  ) {}

  private loaderDialogRef: MatDialogRef<LoaderComponent> | null = null;
  private openDialogs = new Map<ComponentType<any> | TemplateRef<any>, MatDialogRef<any>>();

  open<T, D = any, R = any>(
    componentOrTemplate: ComponentType<T> | TemplateRef<T>,
    config?: MatDialogConfig<D>
  ): MatDialogRef<T, R> | null {

    // Verificar si ya existe un diálogo abierto para este componente o plantilla
    if (this.openDialogs.has(componentOrTemplate)) {
      console.warn('Este diálogo ya está abierto:', componentOrTemplate);
      return null;
    }

    // Abre el diálogo usando el MatDialog original
    const dialogRef = this.dialog.open(componentOrTemplate, config);
    this.openDialogs.set(componentOrTemplate, dialogRef);

    this.onDialogOpen();

    // Suscribe a afterClosed para manejar la lógica genérica al cerrar
    dialogRef.afterClosed().subscribe((result) => {
      this.openDialogs.delete(componentOrTemplate);
      if(this.canOpenDialog()){
        this.onDialogClose(result);
      }
    });

    return dialogRef;
  }

  private onDialogOpen(): void {
    this.focusService.setActiveKeyboard('');
    // Aquí puedes incluir cualquier lógica que desees ejecutar al abrir cualquier modal
  }

  private onDialogClose(result: any): void {
    this.focusService.setActiveKeyboard('main-keyboard');
    // Aquí puedes incluir cualquier lógica que desees ejecutar al cerrar cualquier modal
  }

  // Función privada para verificar si hay diálogos abiertos
  public canOpenDialog(): boolean {
    return this.dialog.openDialogs.length === 0;
  }

  showSuccess(title?: string, text?: string) {

    const titleData = title ? title : 'La acción ha sido completada con éxito';
    const textData = text ? text : '';

    // Verifica si ya hay un diálogo abierto con el tipo 'success'
    const isDialogAlreadyOpen = this.dialog.openDialogs.some(dialog => dialog.componentInstance.data?.type === 'success');

    if (!isDialogAlreadyOpen) {
      const dialogRef = this.dialog.open(DialogComponent, {
        data: { title: titleData, text: textData, type: 'success', actions: false },
      });

      // Cerrar automáticamente después de 10 segundos si no está ya cerrado
      const timeout = setTimeout(() => {
        dialogRef.close();
      }, 10000);

      // Cancelar el cierre automático si el modal se cierra antes
      dialogRef.afterClosed().subscribe(() => clearTimeout(timeout));
    }
  }

  showError(title?: string, text?: string) {

    let titleData = title ? title : '¡No se puedo completar la operación!';
    let textData = text ? text : '';

    // Verifica si ya hay un diálogo abierto con el tipo 'danger'
    const isDialogAlreadyOpen = this.dialog.openDialogs.some(dialog => dialog.componentInstance.data?.type === 'danger');

    if (!isDialogAlreadyOpen) {
      const dialogRef = this.dialog.open(DialogComponent, {
        data: { title: titleData, text: textData, type: 'danger', actions: false },
      });

      // Cerrar automáticamente después de 10 segundos si no está ya cerrado
      const timeout = setTimeout(() => {
        dialogRef.close();
      }, 60000);

      // Cancelar el cierre automático si el modal se cierra antes
      dialogRef.afterClosed().subscribe(() => clearTimeout(timeout));
    }
  }

  displayDialog({
    title = '¡Atención!',
    text = '',
    type = 'info',
    closeTime = null
  }: {
    title?: string,
    text?: string,
    type?: string,
    closeTime?: number | null
  }) {

    let titleData = title ? title : '¡Atención!';
    let textData = text ? text : '';

    // if (this.canOpenDialog()) {
    const dialogRef =  this.dialog.open(DialogComponent, {
        data: { title: titleData, text: textData, type: type, actions: false },
      });
    // }

    if(closeTime){
      const secondsInMs = closeTime * 1000; // 120,000 ms

      // Cerrar automáticamente después de 10 segundos si no está ya cerrado
      const timeout = setTimeout(() => {
        dialogRef.close();
      }, secondsInMs);

      // Cancelar el cierre automático si el modal se cierra antes
      dialogRef.afterClosed().subscribe(() => clearTimeout(timeout));
    }
  }

  showLoader(title?: string, text?: string) {
    if (this.loaderDialogRef) {
        return;
    }

    let titleData = title ? title : 'Cargando';
    let textData = text ? text : '';

    this.loaderDialogRef = this.dialog.open(LoaderComponent, {
      data: { title: titleData, text: textData },
      disableClose: true
    });
  }

  closeLoader(){
    if (this.loaderDialogRef && this.loaderDialogRef.getState() !== MatDialogState.CLOSED) {
      this.loaderDialogRef.close();
      this.loaderDialogRef = null;
    }
  }

  displayValidateDialog(title?: string, text?: string, type: string = 'info'): Promise<boolean> {
    let titleData = title ? title : '¡Atención!';
    let textData = text ? text : '';

    const isDialogAlreadyOpen = this.dialog.openDialogs.some(dialog => dialog.componentInstance.data?.type === 'info');

    if (!isDialogAlreadyOpen) {
      const dialogRef = this.dialog.open(DialogComponent, {
        data: { title: titleData, text: textData, type: type, actions: true },
      });

      return new Promise((resolve) => {
        dialogRef.afterClosed().subscribe(result => {
          resolve(result);
        });
      });
    }
    else {
      return Promise.resolve(false); // Si ya hay un diálogo abierto, devolvemos false
    }
  }

  showCollect() {
    let titleData = 'En espera de colector';
    let textData = '';

    const isDialogAlreadyOpen = this.dialog.openDialogs.some(
      dialog => dialog.componentInstance.data?.type === 'collect'
    );

    if (!isDialogAlreadyOpen) {
      const dialogRef = this.dialog.open(DialogComponent, {
        data: { title: titleData, text: textData, type: 'collect', actions: false },
        disableClose: true,
        closeOnNavigation: false
      });

      let closedByTimeout = false;

      const twoMinutesInMs = 2 * 60 * 1000; // 120,000 ms

      const timeout = setTimeout(() => {
        closedByTimeout = true; // Marca que se cerró por el timeout
        dialogRef.close('timeout'); // Envía un valor indicando la razón
      }, twoMinutesInMs);

      // Retorna el observable del cierre del diálogo
      return dialogRef.afterClosed().pipe(
        tap(() => clearTimeout(timeout)), // Limpia el timeout al cerrar el diálogo
        map(result => {
          // Si se cerró por timeout, devolver un mensaje; de lo contrario, null
          if (closedByTimeout) {
            return 'Dialog closed by timeout';
          }
          return result; // Devuelve el resultado (o undefined) si se cerró manualmente
        })
      );
    } else {
      // Si ya está abierto, retornar un observable vacío
      return of(null);
    }
  }

  closeCollectDialog() {
    // Filtra los diálogos abiertos para encontrar los que tienen el tipo 'collect'
    this.dialog.openDialogs.forEach(dialog => {
      if (dialog.componentInstance.data?.type === 'collect') {
        dialog.close(); // Cierra el diálogo
      }
    });
  }

}
